import type { ActingModifier } from '@/shared/api'
import type { AccessPair } from '../access'

/**
 * Форматы — те же, что у `actingModifierSchema` бэка: кривое значение из адреса отбрасывается,
 * а не уходит в запрос за `400`.
 */
const CLIENT = /^.{1,50}$/

/** Код клиента в формате бэка — для модификатора из строки запроса и из пути профиля. */
export function isClientCode(value: string | undefined): value is string {
  return value !== undefined && CLIENT.test(value)
}
const ACCOUNT = /^\d{1,10}$/
const UNP = /^\d{9}$/
const CART = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const SETTLEMENTS = ['cash', 'cashless-byn'] as const

type Settlement = (typeof SETTLEMENTS)[number]

function isSettlement(value: string): value is Settlement {
  return (SETTLEMENTS as readonly string[]).includes(value)
}

function pick(params: URLSearchParams, name: string, format: RegExp): string | undefined {
  const value = params.get(name)
  return value !== null && format.test(value) ? value : undefined
}

/**
 * Модификатор из адреса страницы — только параметры, которые принимает ручка пары. Правила бэка:
 * `account`, `unp`, `settlement` — только вместе с `client`; `cart` — только без него (при обоих
 * побеждает `client`). Права здесь не проверяются — это делает `useActingMode`.
 */
export function readActingModifier(
  params: URLSearchParams,
  pair: AccessPair,
): ActingModifier | undefined {
  const accepts = pair.accepts ?? []
  const client = pick(params, 'client', CLIENT)

  if (client === undefined) {
    const cart = accepts.includes('cart') ? pick(params, 'cart', CART) : undefined
    return cart === undefined ? undefined : { cart }
  }

  const modifier: ActingModifier = { client }
  const account = accepts.includes('account') ? pick(params, 'account', ACCOUNT) : undefined
  const unp = accepts.includes('unp') ? pick(params, 'unp', UNP) : undefined
  const settlement = accepts.includes('settlement') ? params.get('settlement') : null
  if (account !== undefined) modifier.account = account
  if (unp !== undefined) modifier.unp = unp
  if (settlement !== null && isSettlement(settlement)) modifier.settlement = settlement
  return modifier
}

/**
 * Модификатор, который переносится в ссылку на другой раздел: `client` — если у цели есть право
 * «от имени», остальные параметры — если цель их принимает. Корзина живёт только в каталоге.
 */
export function carriedModifier(
  modifier: ActingModifier | undefined,
  target: AccessPair,
): ActingModifier | undefined {
  if (modifier === undefined || target.onBehalf === undefined) return undefined
  const accepts = target.accepts ?? []
  if (modifier.client === undefined) {
    return modifier.cart !== undefined && accepts.includes('cart')
      ? { cart: modifier.cart }
      : undefined
  }
  const carried: ActingModifier = { client: modifier.client }
  if (modifier.account !== undefined && accepts.includes('account'))
    carried.account = modifier.account
  if (modifier.unp !== undefined && accepts.includes('unp')) carried.unp = modifier.unp
  if (modifier.settlement !== undefined && accepts.includes('settlement')) {
    carried.settlement = modifier.settlement
  }
  return carried
}

/** Строка запроса модификатора — для ссылок и `query()` endpoint'ов. Пустая — `''`. */
export function actingSearch(modifier: ActingModifier | undefined): string {
  if (modifier === undefined) return ''
  const params = new URLSearchParams()
  for (const key of ['client', 'account', 'unp', 'settlement', 'cart'] as const) {
    const value = modifier[key]
    if (value !== undefined) params.set(key, value)
  }
  const search = params.toString()
  return search === '' ? '' : `?${search}`
}
